import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Gift,
    Copy,
    Check,
    Send,
    MessageSquare,
    Mail,
    Award,
    ShieldCheck
} from 'lucide-react';
import DefaultSEO from '../components/DefaultSEO';

const InviteAndEarn = () => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const referralCode = "ZOYO500REF";

    const handleCopy = () => {
        navigator.clipboard.writeText(referralCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const shareOptions = [
        { name: 'WhatsApp', icon: MessageSquare, color: 'bg-success text-white', url: `https://api.whatsapp.com/send?text=Join%20me%20on%20ZoyoTrip%20and%20get%20%E2%82%B9500%20bonus%20on%20signup%21%20Use%20my%20code%3A%20${referralCode}` },
        { name: 'Telegram', icon: Send, color: 'bg-info text-white', url: `https://t.me/share/url?url=https://zoyotrip.com&text=Join%20me%20on%20ZoyoTrip%20and%20get%20%E2%82%B9500%20bonus%20on%20signup%21%20Use%20code%3A%20${referralCode}` },
        { name: 'Email', icon: Mail, color: 'bg-secondary text-white', url: `mailto:?subject=Get%20%E2%82%B9500%20ZoyoTrip%20Bonus&body=Join%20me%20on%20ZoyoTrip%20and%20get%20%E2%82%B9500%20bonus%20on%20signup%21%20Use%20my%20referral%20code%3A%20${referralCode}` }
    ];

    const steps = [
        {
            number: "1",
            title: "Share your code",
            desc: "Send your unique invite code or link to friends."
        },
        {
            number: "2",
            title: "Friend signs up",
            desc: "Your friend gets ₹500 immediately credited into their Zoyo Wallet."
        },
        {
            number: "3",
            title: "You get rewarded",
            desc: "You receive ₹500 wallet credit as soon as their first trip is completed."
        }
    ];


    return (
        <div className="container py-4 min-vh-100 d-flex flex-column align-items-center" style={{ maxWidth: '750px' }}>
            <DefaultSEO title="Invite & Earn | ZoyoTrip" description="Refer your friends to ZoyoTrip and earn rewards on your next hotel booking." />
            {/* Header */}
            <button
                className="btn align-self-start btn-link text-dark text-decoration-none p-0 mb-4 d-flex align-items-center"
                onClick={() => navigate('/account')}
            >
                <ArrowLeft size={20} className="me-2" />
                <h4 className="fw-bold mb-0">Invite & Earn</h4>
            </button>

            {/* Glowing Hero Banner */}
            <div
                className="w-100 rounded-4 p-4 p-md-5 text-white mb-4 position-relative overflow-hidden shadow-lg border border-opacity-10 border-white text-center"
                style={{
                    background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)'
                }}
            >
                <div className="position-absolute bg-emerald-300 rounded-full opacity-20 filter blur-3xl" style={{ width: '200px', height: '200px', top: '-100px', right: '-50px' }}></div>
                <div className="position-absolute bg-teal-400 rounded-full opacity-10 filter blur-3xl" style={{ width: '200px', height: '200px', bottom: '-100px', left: '-50px' }}></div>

                <div className="bg-white bg-opacity-20 d-inline-flex p-3 rounded-full mb-3 backdrop-blur-md">
                    <Gift size={40} className="text-yellow-300 animate-pulse" />
                </div>

                <h2 className="fw-extrabold fs-1 mb-2">Spread the Joy, Share the Trip!</h2>
                <p className="lead opacity-90 fs-6 mb-4 max-w-lg mx-auto">
                    Get your friends to experience luxury travel. You both earn <span className="fw-bold text-yellow-300">₹500</span> when they sign up and complete their first booking!
                </p>

                {/* Referral Box */}
                <div className="bg-white bg-opacity-10 p-3 rounded-3 border border-white border-opacity-15 d-inline-flex align-items-center gap-3 backdrop-blur-sm max-w-sm w-100 justify-content-between">
                    <div className="text-start ps-2">
                        <span className="text-[10px] text-uppercase opacity-75 fw-bold block mb-0.5">Your Invite Code</span>
                        <span className="font-mono fw-extrabold fs-5 tracking-wider text-yellow-300">{referralCode}</span>
                    </div>
                    <button
                        onClick={handleCopy}
                        className={`btn ${copied ? 'btn-success' : 'btn-light'} fw-bold d-flex align-items-center gap-1.5 px-3.5 py-2 rounded-2.5 transition`}
                    >
                        {copied ? (
                            <><Check size={16} /> Copied</>
                        ) : (
                            <><Copy size={16} /> Copy</>
                        )}
                    </button>
                </div>
            </div>

            {/* Quick Share Grid */}
            <div className="card shadow-sm border-0 w-100 mb-4 rounded-4">
                <div className="card-body p-4 text-center">
                    <h5 className="fw-bold mb-3 text-dark">Quick Share link</h5>
                    <div className="d-flex justify-content-center gap-3">
                        {shareOptions.map((opt) => {
                            const Icon = opt.icon;
                            return (
                                <a
                                    key={opt.name}
                                    href={opt.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`btn ${opt.color} d-flex align-items-center justify-content-center gap-2 px-4 py-2.5 rounded-pill shadow-sm transition hover-scale`}
                                    style={{ transition: 'all 0.2s ease' }}
                                >
                                    <Icon size={18} />
                                    <span className="fw-bold text-sm">{opt.name}</span>
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* How It Works Steps */}
            <div className="card shadow-sm border-0 w-100 mb-4 rounded-4">
                <div className="card-body p-4">
                    <h5 className="fw-bold mb-4 text-dark d-flex align-items-center gap-2">
                        <Award className="text-primary" size={20} /> How Invite & Earn Works
                    </h5>

                    <div className="row g-4">
                        {steps.map((step, idx) => (
                            <div key={idx} className="col-md-4 text-center">
                                <div className="d-flex flex-column align-items-center">
                                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full d-flex align-items-center justify-content-center fw-extrabold mb-3 border border-emerald-100 shadow-sm fs-5">
                                        {step.number}
                                    </div>
                                    <h6 className="fw-bold text-gray-900 mb-2">{step.title}</h6>
                                    <p className="text-xs text-muted lh-base px-2">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Live Earnings & Stats */}


            {/* Trust Footer */}
            <div className="d-flex align-items-center gap-2 text-muted mt-4 justify-content-center">
                <ShieldCheck className="text-emerald-500" size={16} />
                <span className="text-xs fw-semibold">Rewards are fully backed by ZoyoTrust Travel Guarantee</span>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .hover-scale:hover {
                    transform: translateY(-2px);
                }
            `}} />
        </div>
    );
};

export default InviteAndEarn;